import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { searchMovies, getHotMovies } from "@/utils/movie";
import { typeCategories } from "@/mocks/site";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const activeType = params.get("type") || "";
  const [input, setInput] = useState(q);

  useSeo({
    title: q ? `搜索“${q}” - 欲望的诱惑` : "搜索影视 - 欲望的诱惑",
    description: q
      ? `在欲望的诱惑中搜索与“${q}”相关的影视作品、演员与剧情介绍。`
      : "输入关键词，快速搜索欲望的诱惑站内的影视作品、演员与剧情介绍。",
    keywords: `影视搜索,${q || "影视"},剧情介绍,演员表,欲望的诱惑`,
    canonicalPath: "/search",
  });

  const results = useMemo(() => {
    const base = searchMovies(q);
    return activeType ? base.filter((m: any) => m.type === activeType) : base;
  }, [q, activeType]);

  const submit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set("q", input.trim());
    if (activeType) next.set("type", activeType);
    setParams(next);
  };

  const setType = (type: string) => {
    const next = new URLSearchParams(params);
    if (type === activeType || !type) next.delete("type");
    else next.set("type", type);
    setParams(next);
  };

  const hot = getHotMovies(5);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-4">
              <Link href="/" to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">搜索</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">搜索影视内容</h1>
            <p className="mt-2.5 text-sm md:text-base text-foreground-600">
              支持按影视名称、导演、演员、类型与关键词进行模糊匹配。
            </p>

            <form onSubmit={submit} className="mt-6 max-w-2xl">
              <div className="flex items-center rounded-full bg-background-50 border border-background-300/70 pl-4 pr-1.5 py-1.5 focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line text-lg text-foreground-400"></i>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="输入影视名称、演员或关键词…"
                  className="flex-1 bg-transparent outline-none text-sm md:text-base px-3 text-foreground-900 placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-search-2-line"></i>
                  搜索
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <aside className="w-full lg:w-[240px] shrink-0">
              <div className="lg:sticky lg:top-24 bg-background-50 border border-background-200/70 rounded-lg p-5">
                <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-500"></i>
                  按类型筛选
                </h3>
                <div className="flex flex-wrap lg:flex-col gap-2">
                  <button
                    onClick={() => setType("")}
                    className={`px-3 py-2 rounded-md text-left text-sm transition-colors cursor-pointer ${
                      !activeType
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    全部类型
                  </button>
                  {typeCategories.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => setType(c.key)}
                      className={`px-3 py-2 rounded-md text-left text-sm transition-colors cursor-pointer inline-flex items-center gap-2 ${
                        activeType === c.key
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:text-primary-600"
                      }`}
                    >
                      <i className={c.icon}></i>
                      {c.key}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              {q ? (
                <p className="text-sm text-foreground-600 mb-5">
                  关键词 <span className="text-primary-600 font-semibold">“{q}”</span> 共找到{" "}
                  <span className="text-primary-600 font-semibold">{results.length}</span> 条结果
                </p>
              ) : (
                <p className="text-sm text-foreground-600 mb-5">输入关键词开始搜索吧。</p>
              )}

              {results.length > 0 ? (
                <div className="space-y-3 md:space-y-4">
                  {results.map((m: any) => (
                    <MovieCard key={m.id} movie={m} variant="list" />
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                    {q ? "没有找到相关影视内容" : "还没有搜索内容"}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-500">
                    {q ? "换个关键词试试，或从下方热门影视中挑选。" : "在上方输入框输入影视名称、演员或关键词。"}
                  </p>
                </div>
              )}

              <div className="mt-12">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i>
                  热门影视推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                  {hot.map((m: any) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}